import { useEffect, useRef, useState } from 'react';
import { ArrowDown, Sparkles, Code, Zap, Globe } from 'lucide-react';
import GradientButton from '../ui/GradientButton';
import GlowingText from '../ui/GlowingText';

const HeroSection = ({ currentTypeText, onScrollToProjects }) => {
  const heroRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    const heroElement = heroRef.current;
    if (heroElement) {
      heroElement.addEventListener('mousemove', handleMouseMove);
      return () => heroElement.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (    <section 
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, 
          rgba(99, 102, 241, 0.1) 0%, 
          rgba(14, 165, 233, 0.05) 30%, 
          transparent 60%)`
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating geometric shapes */}
        <div className="absolute top-20 left-20 w-2 h-2 bg-gradient-to-r from-violet-400 to-blue-400 rounded-full animate-float opacity-60"></div>
        <div className="absolute top-40 right-32 w-3 h-3 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full animate-float opacity-40" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-40 w-1 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full animate-float opacity-80" style={{ animationDelay: '2s' }}></div>
        
        {/* Gradient orbs */}
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-gradient-to-r from-violet-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-gradient-to-r from-violet-500/10 to-blue-500/10 border border-violet-500/20 backdrop-blur-sm transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Sparkles className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-medium text-violet-300">Available for new opportunities</span>
        </div>

        {/* Main Heading */}
        <h1 className={`text-display-2xl font-bold mb-6 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>          <span className="block">
            <GlowingText 
              color="mixed"
              size="3xl"
              className="bg-gradient-to-r from-white via-violet-200 to-blue-200 bg-clip-text text-transparent"
            >
              Designer Developer
            </GlowingText>
          </span>
          <span className="block mt-2 text-heading-xl text-neutral-300">
            Building Digital Excellence
          </span>
        </h1>

        {/* Typewriter Text */}
        <div className={`mb-8 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-body-lg text-neutral-400 mb-4">
            Full-stack developer specializing in creating
          </p>
          <div className="text-heading-md">
            <span className="bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent font-semibold">
              {currentTypeText}
            </span>
            <span className="inline-block w-0.5 h-6 bg-violet-400 ml-1 animate-pulse"></span>
          </div>
        </div>

        {/* Feature Pills */}
        <div className={`flex flex-wrap justify-center gap-4 mb-12 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {[
            { icon: Code, label: "Clean Code", color: "from-violet-500/20 to-violet-500/5" },
            { icon: Zap, label: "Fast Performance", color: "from-blue-500/20 to-blue-500/5" },
            { icon: Globe, label: "Modern Stack", color: "from-emerald-500/20 to-emerald-500/5" },
          ].map((item, index) => (
            <div
              key={item.label}
              className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${item.color} border border-white/10 backdrop-blur-sm hover:scale-105 transition-transform duration-300 group`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <item.icon className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" />
              <span className="text-sm text-white/70 group-hover:text-white transition-colors">
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>          <GradientButton
            size="lg"
            color="primary"
            variant="solid"
            onClick={onScrollToProjects}
            className="group"
          >
            <span>View My Work</span>
            <ArrowDown className="w-5 h-5 ml-2 group-hover:translate-y-1 transition-transform duration-300" />
          </GradientButton>
          
          <GradientButton
            variant="outline"
            size="lg"
            color="accent"
            onClick={() => window.location.href = 'mailto:contact@example.com'}
            className="hover:bg-white/5"
          >
            Get In Touch
          </GradientButton>
        </div>
      </div>

      {/* Decorative Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
    </section>
  );
};

export default HeroSection;